import { useState, MouseEvent, TouchEvent } from "react";
import { Button } from "@/components/ui/button"; // Sesuaikan path jika berbeda
import { ZoomIn, ZoomOut, RotateCw, RefreshCcw, Move } from "lucide-react";

export default function ImageControl({ imageUrl }: { imageUrl: string }) {
  // Transform states
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Drag states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // --- CONTROLS ---
  const zoomIn = () => setScale((prev) => prev + 0.2);
  const zoomOut = () => setScale((prev) => (prev > 0.4 ? prev - 0.2 : prev));
  const rotate = () => setRotation((prev) => prev + 90);
  const reset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  // --- MOUSE EVENTS (PC) ---
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    // Simpan posisi awal kursor dikurangi posisi gambar saat ini
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  // --- TOUCH EVENTS (MOBILE) ---
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const touch = e.touches[0];
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  return (
    <div className="relative h-full w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex flex-col">
      {/* TOOLBAR CONTROLS */}
      <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md p-1.5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800">
        <Button
          variant="ghost"
          size="icon"
          onClick={zoomIn}
          className="h-8 w-8 hover:bg-slate-200 dark:hover:bg-slate-800"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4 text-slate-700 dark:text-slate-300" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={zoomOut}
          className="h-8 w-8 hover:bg-slate-200 dark:hover:bg-slate-800"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4 text-slate-700 dark:text-slate-300" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={rotate}
          className="h-8 w-8 hover:bg-slate-200 dark:hover:bg-slate-800"
          title="Rotate"
        >
          <RotateCw className="w-4 h-4 text-slate-700 dark:text-slate-300" />
        </Button>
        <div className="h-px bg-slate-200 dark:bg-slate-700 mx-1 my-0.5" />
        <Button
          variant="ghost"
          size="icon"
          onClick={reset}
          className="h-8 w-8 hover:bg-slate-200 dark:hover:bg-slate-800 text-rose-500 hover:text-rose-600"
          title="Reset"
        >
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </div>

      {/* INDIKATOR DRAG (Muncul saat di-zoom) */}
      {scale > 1 && (
        <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-black/50 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-medium pointer-events-none">
          <Move className="w-3.5 h-3.5" />
          <span>Bisa digeser</span>
        </div>
      )}

      {/* IMAGE CONTAINER (Menerima event Mouse & Touch) */}
      <div
        className={`relative flex-1 w-full h-full flex items-center justify-center overflow-hidden touch-none select-none ${
          isDragging
            ? "cursor-grabbing"
            : scale > 1
              ? "cursor-grab"
              : "cursor-default"
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUpOrLeave}
      >
        <img
          src={imageUrl}
          alt="preview"
          draggable={false} // Cegah default drag behavior dari browser
          className={`max-h-full max-w-full object-contain pointer-events-none ${
            // Matikan transisi SAAT dragging agar gambar tidak lag/delay mengikuti kursor
            isDragging
              ? "transition-none"
              : "transition-transform duration-300 ease-out"
          }`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
          }}
        />
      </div>
    </div>
  );
}
